"use client"

import { sendBlockEvent } from "@/utils/gtm"
import { replaceRichTextLinks } from "@/utils/replaceRichTextLinks"
import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import { FormDto } from "api"
import { submitUmbracoForm } from "api/formsApi"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { useState } from "react"
import { colors, Container, mediaQueries, sizes } from "ui"
import FailureState from "../Component/CopyOfPolicyBlock/FailureState"
import SuccessState from "../Component/CopyOfPolicyBlock/SuccessState"
import { RelatedFields } from "../Component/FormBlock/service"
import FormBuilder from "./FormBuilder"
import { ExternalApiFormStoreProvider } from "./FormContext"
import { LoadFormGlobalStore } from "./LoadFormGlobalStore"
import { RelatedFieldsProvider } from "./RelatedFieldsInFromContext"
import convertFormData from "./utils/convertFormData"

const StyledTitleWrapper = styled.div`
  padding-top: 40px;
  background-color: ${colors.white};
  color: ${colors?.red};
  border-top-right-radius: 8px;
  border-top-left-radius: 8px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`
const StyledFormWrapper = styled.div`
  padding: 24px 32px 40px 32px;
  background-color: ${colors?.white};
  border-bottom-right-radius: 8px;
  border-bottom-left-radius: 8px;
  a {
    color: ${colors.black};
  }
`
const StyledSubmission = styled.div`
  padding-top: 24px;
  display: flex;
  justify-content: center;
  color: ${colors.black};
`
const StyledContainer = styled(Container)`
  position: relative;
  width: 100%;

  ${mediaQueries.desktopM} {
    width: 480px;
  }
`
const StyledFormTitle = styled(Typography)`
  font-size: 34px;
  font-weight: 700;
  text-align: center;
`
const StyledShapeWrapper = styled.div`
  position: absolute;
  right: -70px;
  top: -25px;
  width: 629px;
  height: 622px;
  z-index: -1;
`
const StyledBlockCardContainerWithShape = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: 0 auto;
  padding: ${sizes.baseTriple};
  border-radius: 30px;
  background-color: ${colors.white};
  gap: ${sizes.baseDouble};
  box-shadow: -4px 8px 30px 0px rgba(2, 1, 64, 0.12);
  position: relative;
  margin-top: 70px;

  ${mediaQueries.desktop} {
    padding: ${sizes.baseSix};
    margin-top: 70px;
  }
`
type FormMainProps = {
  data: FormDto
  locale: string
  formTitle: string
  linkRichText?: string
  blockId?: string
  blockTitle?: string
  formDefaultInsuranceType?: string
  additionalData?: any
  relatedSelectFieldsDict?: RelatedFields
}

const FormMain = ({
  data,
  formTitle,
  locale,
  linkRichText,
  blockId,
  blockTitle,
  formDefaultInsuranceType,
  additionalData,
  relatedSelectFieldsDict,
}: FormMainProps) => {
  const [formState, setFormState] = useState<"idle" | "success" | "error">(
    "idle",
  )
  const [formData, setFormData] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const t = useTranslations("DynamicForms")

  // Create default data structures for SuccessState and FailureState
  const successStateData = {
    successImage: {
      mediaItems: [
        {
          url: "/popup-images/popup3.png", // Using popup3.png from popup-images directory
        },
      ],
    },
    successTitle: {
      value: t("successMessage") || "הטופס נשלח בהצלחה!",
    },
    successText: {
      value: t("successDescription"),
    },
  }

  const failureStateData = {
    failImage: {
      mediaItems: [
        {
          url: "/icons/closeIcon.svg", // Using existing close icon for errors
        },
      ],
    },
    failTitle: {
      value: t("errorTitle"),
    },
    failText: {
      value: t("ErrorMessage") || "אירעה שגיאה בשליחת הטופס. אנא נסה שנית.",
    },
    retryFailText: {
      value: t("retryText"),
    },
    serviceFailText: {
      value: t("serviceErrorText"),
    },
  }

  const sendForm = async (convertedData: any) => {
    const response = await submitUmbracoForm(data.id, {
      culture: locale === "he" ? "he-il" : "en-us",
      values: convertedData.values,
    })
    return response
  }

  const handleFormSubmit = async (formData: any) => {
    try {
      setLoading(true)
      const convertedData = convertFormData(formData, data)
      setFormData(formData)
      const response = await sendForm(convertedData)
      if (response && response.data && response.data.messageOnSubmit) {
        setFormState("success")
        sendBlockEvent({
          blockId: blockId || "",
          title: blockTitle,
          componentName: t("componentName"),
          productName: formDefaultInsuranceType || undefined,
          actionName: t("actionName"),
          buttonName: data?.submitLabel || undefined,
          eventPrefix: "lead",
        })
      } else {
        setFormState("error")
      }
      setLoading(false)
    } catch (error) {
      setLoading(false)
      // eslint-disable-next-line no-console
      console.error("Error submitting form", error)
      setFormData(formData)
      setFormState("error")
    }
  }

  const handleRetry = () => {
    setFormState("idle")
  }

  const handleGoHome = () => {}
  return (
    <StyledContainer withoutPadding>
      <StyledShapeWrapper>
        <Image
          fill
          src={"/formBackgroundShape.svg"}
          alt="form Background Shape"
        />
      </StyledShapeWrapper>
      <div
        style={{
          display: formState === "idle" ? "block" : "none",
          width: "100%",
        }}
      >
        <StyledTitleWrapper>
          <StyledFormTitle variant="h5" color={colors.red}>
            {formTitle}
          </StyledFormTitle>
        </StyledTitleWrapper>
        <StyledFormWrapper>
          <ExternalApiFormStoreProvider>
            <RelatedFieldsProvider>
              <LoadFormGlobalStore
                additionalData={additionalData}
                relatedSelectFieldsDict={relatedSelectFieldsDict}
              />
              <FormBuilder
                data={data}
                locale={locale}
                onSubmit={handleFormSubmit}
                defaultValues={formData}
                loading={loading}
              />
            </RelatedFieldsProvider>
          </ExternalApiFormStoreProvider>
          {linkRichText && (
            <StyledSubmission
              dangerouslySetInnerHTML={{
                __html: replaceRichTextLinks(linkRichText),
              }}
            ></StyledSubmission>
          )}
        </StyledFormWrapper>
      </div>
      {(formState === "success" || formState === "error") && (
        <StyledBlockCardContainerWithShape>
          {formState === "success" && <SuccessState data={successStateData} />}
          {formState === "error" && (
            <FailureState data={failureStateData} onRetry={handleRetry} />
          )}
        </StyledBlockCardContainerWithShape>
      )}
    </StyledContainer>
  )
}

export default FormMain
